import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import React, { useState } from 'react';

import {
  AssetDetails,
  assetDetailsSchema,
  Expectation,
  ScenarioEnum,
} from '@/api/asset/asset-schema';
import { AssetCard } from '@/components/asset-card/AssetCard';
import { EarningsDate } from '@/components/earnings-date/EarningsDate';
import { EarningsTable } from '@/components/earnings-table/EarningsTable';
import { EarningsWidget } from '@/components/earnings-widget/EarningsWidget';
import { getAssetSeo } from '@/components/seo/getAssetSeo';
import { SEO, SEOProps } from '@/components/seo/SEO';
import {
  apiRoutes,
  DefaultAssetProperties,
  FullAssetProperties,
} from '@/utils/api-routes';
import { getInitialExpectation, getUserLocale } from '@/utils/asset/assetUtils';
import { validateFetcher } from '@/utils/validateFetcher';
import * as S from '~/styles/asset/Asset.styles';

import { mapAssetData } from '../src/utils/status/status';

type ParsePathString = (
  path: string
) => DefaultAssetProperties | FullAssetProperties | null;

export const parsePathString: ParsePathString = pathString => {
  try {
    const properties = pathString.split('-');

    if (properties.length === 2) {
      return {
        exchange: properties[0],
        ticker: properties[1],
      };
    }
    if (properties.length === 4) {
      return {
        exchange: properties[0],
        ticker: properties[1],
        year: properties[2],
        period: properties[3],
      };
    }
    return null;
  } catch (error) {
    return null;
  }
};

interface AssetSEO extends Required<SEOProps> {
  twitterMessage: {
    preReported: string;
    postReported: string;
  };
}

export const getServerSideProps: GetServerSideProps<{
  assetDetails: AssetDetails;
  seo: AssetSEO;
  initExpectation: Expectation;
  locale: string;
}> = async ({ params, query, resolvedUrl, locale, req, res }) => {
  const pathProperties = parsePathString(params?.asset as string);
  res.setHeader(
    'Cache-Control',
    'public, s-maxage=30, stale-while-revalidate=59'
  );

  if (!pathProperties) {
    return {
      notFound: true,
    };
  }

  try {
    const assetDetails = await validateFetcher<AssetDetails>({
      endpoint: apiRoutes.getSecurityUrl(pathProperties),
      schema: assetDetailsSchema,
    });

    const initExpectation = getInitialExpectation(query, assetDetails);

    const seo = getAssetSeo(assetDetails, resolvedUrl, initExpectation);
    return {
      props: {
        ...(await serverSideTranslations(locale as string, [
          'earnings',
          'common',
        ])),
        assetDetails,
        seo,
        initExpectation,
        locale: getUserLocale(req.headers['accept-language'], locale as string),
      },
    };
  } catch (error) {
    return {
      notFound: true,
    };
  }
};

export type SecurityProps = InferGetServerSidePropsType<
  typeof getServerSideProps
>;

export default function Security({
  assetDetails,
  seo,
  initExpectation,
  locale,
}: SecurityProps) {
  const [activeScenario, setActiveScenario] = useState<ScenarioEnum>(
    ScenarioEnum.Beat
  );
  const assetDetailsWithStatus = mapAssetData(assetDetails, activeScenario);

  return (
    <S.Container data-testid="earnings-page">
      <SEO {...seo} />
      <S.Details>
        <S.AssetInfo>
          <AssetCard assetDetails={assetDetailsWithStatus} />
          <EarningsDate
            reporting_date={assetDetailsWithStatus.reporting_date}
            reported={assetDetailsWithStatus.reported}
            locale={locale}
          />
        </S.AssetInfo>
        <S.TableWrapper>
          <EarningsTable assetDetails={assetDetailsWithStatus} />
        </S.TableWrapper>
      </S.Details>
      <S.Widget>
        <EarningsWidget
          assetDetails={assetDetailsWithStatus}
          activeScenario={activeScenario}
          setActiveScenario={setActiveScenario}
          initExpectation={initExpectation}
          chartAlt={seo.og.imageAlt}
          twitterMessage={seo.twitterMessage}
          url={seo.url}
        />
      </S.Widget>
    </S.Container>
  );
}
